import React, { useState } from "react";
import { Card, CardBody, Form, FormGroup, Label, Input, InputGroup, Button, Row, Col, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import useProjects from "../../Hooks/useProjects";
import useStages from "../../Hooks/useStages";
import useUsersWithLeads from "../../Hooks/useUsersWithLeads";
import { createGenericLeadApi } from "../../apiServices/genericLeadServices";

const COUNTRY_CODES = [
  { label: "+91 (IN)", value: "+91" },
  { label: "+1 (US/CA)", value: "+1" },
  { label: "+44 (UK)", value: "+44" },
  { label: "+971 (UAE)", value: "+971" },
  { label: "+966 (KSA)", value: "+966" },
  { label: "+61 (AU)", value: "+61" },
  { label: "+65 (SG)", value: "+65" },
];

const SOURCE_OPTIONS = [
  { label: "Generic Lead", value: "generic" },
  { label: "Manual Entry", value: "manual" },
  { label: "Direct Call", value: "direct_call" },
  { label: "Ai calling", value: "ai_calling" },
  { label: "Ivr calling", value: "ivr_calling" },
  { label: "RCS msg", value: "rcs_msg" },
  { label: "Whatsapp", value: "whatsapp" },
  { label: "Referral", value: "referral" },
  { label: "Walk-in", value: "walk_in" },
  { label: "Website", value: "website" },
  { label: "Facebook", value: "facebook" },
  { label: "Google", value: "google" },
  { label: "Other", value: "other" },
];

const LEAD_TYPE_OPTIONS = [
  { label: "Buyer", value: "Buyer" },
  { label: "Investor", value: "Investor" },
  { label: "Seller", value: "Seller" },
  { label: "Broker", value: "Broker" },
  { label: "Channel Partner", value: "Channel Partner" },
  { label: "Tenant", value: "Tenant" },
  { label: "Property RM", value: "Property RM" },
  { label: "Other", value: "Other" },
];

const CreateLeadPage = () => {
  const navigate = useNavigate();
  const authUser = JSON.parse(localStorage.getItem("authUser"))?.response;
  const isSuperAdmin = authUser?.role === "super_admin";

  const { projects = [] } = useProjects({ limit: 1000 });
  const { stages = [] } = useStages({ limit: 1000 });
  const { users = [] } = useUsersWithLeads();

  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+91",
    phone: "",
    email: "",
    source: "generic",
    leadType: "",
    projectId: "",
    stage: "",
    assignedTo: isSuperAdmin ? "" : authUser?._id || "",
  });

  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setFormData((prev) => ({ ...prev, phone: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      return toast.warning("Please enter Lead Full Name.");
    }

    const cleanPhone = formData.phone.replace(/\D/g, "");
    if (!cleanPhone || !/^[6-9]\d{9}$/.test(cleanPhone)) {
      return toast.warning("Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9.");
    }

    const fullPhone = `${formData.countryCode}${cleanPhone}`;

    try {
      setSubmitting(true);
      const payload = {
        name: formData.name.trim(),
        phone: fullPhone,
        email: formData.email.trim(),
        source: formData.source,
        projectId: formData.projectId || null,
        stage: formData.stage || null,
        assignedTo: formData.assignedTo || null,
        remark: formData.leadType || null,
        autoAssign: !formData.assignedTo,
      };

      const res = await createGenericLeadApi(payload);
      toast.success(res?.message || "Lead created successfully!");
      if (isSuperAdmin) {
        navigate("/all-leads");
      } else {
        navigate("/my-leads");
      }
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.message || "Failed to create lead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Leads" breadcrumbItem="Create Lead" />

          <Row className="justify-content-center">
            <Col lg={10}>
              <Card className="shadow-sm border-0">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                      <h4 className="card-title text-primary mb-1">
                        <i className="mdi mdi-account-plus me-2" /> Add New Lead
                      </h4>
                      <p className="text-muted mb-0">Fill in the lead information to add it to the CRM database.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                      <i className="mdi mdi-arrow-left me-1" /> Back
                    </Button>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      {/* Name */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-name" className="fw-semibold">
                            Full Name <span className="text-danger">*</span>
                          </Label>
                          <Input
                            type="text"
                            id="page-lead-name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Enter full name"
                            required
                          />
                        </FormGroup>
                      </Col>

                      {/* Phone with Country Code */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-phone" className="fw-semibold">
                            Mobile Number <span className="text-danger">*</span>
                          </Label>
                          <InputGroup>
                            <Input
                              type="select"
                              name="countryCode"
                              value={formData.countryCode}
                              onChange={handleChange}
                              style={{ maxWidth: "110px" }}
                            >
                              {COUNTRY_CODES.map((c) => (
                                <option key={c.value} value={c.value}>
                                  {c.label}
                                </option>
                              ))}
                            </Input>
                            <Input
                              type="tel"
                              id="page-lead-phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handlePhoneChange}
                              placeholder="10-digit mobile number"
                              maxLength="10"
                              required
                            />
                          </InputGroup>
                          {formData.phone && !/^[6-9]\d{9}$/.test(formData.phone) ? (
                            <small className="text-danger mt-1 d-block" style={{ fontSize: "11px" }}>
                              Must start with 6, 7, 8, or 9 and be 10 digits.
                            </small>
                          ) : (
                            <small className="text-muted mt-1 d-block" style={{ fontSize: "11px" }}>
                              Starting with 6-9 (10 digits).
                            </small>
                          )}
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      {/* Email */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-email" className="fw-semibold">
                            Email Address
                          </Label>
                          <Input
                            type="email"
                            id="page-lead-email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter email address"
                          />
                        </FormGroup>
                      </Col>

                      {/* Source */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-source" className="fw-semibold">
                            Lead Source
                          </Label>
                          <Input
                            type="select"
                            id="page-lead-source"
                            name="source"
                            value={formData.source}
                            onChange={handleChange}
                          >
                            {SOURCE_OPTIONS.map((src) => (
                              <option key={src.value} value={src.value}>
                                {src.label}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      {/* Lead Type */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-type" className="fw-semibold">
                            Lead Type
                          </Label>
                          <Input
                            type="select"
                            id="page-lead-type"
                            name="leadType"
                            value={formData.leadType}
                            onChange={handleChange}
                          >
                            {LEAD_TYPE_OPTIONS.map((lt) => (
                              <option key={lt.value} value={lt.value}>
                                {lt.label}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      {/* Project */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-project" className="fw-semibold">
                            Project
                          </Label>
                          <Input
                            type="select"
                            id="page-lead-project"
                            name="projectId"
                            value={formData.projectId}
                            onChange={handleChange}
                          >
                            <option value="">-- Select Project --</option>
                            {projects.map((proj) => (
                              <option key={proj._id} value={proj._id}>
                                {proj.projectName}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      {/* Stage */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-stage" className="fw-semibold">
                            Initial Stage
                          </Label>
                          <Input
                            type="select"
                            id="page-lead-stage"
                            name="stage"
                            value={formData.stage}
                            onChange={handleChange}
                          >
                            <option value="">-- Default (New Lead) --</option>
                            {stages.map((stg) => (
                              <option key={stg._id} value={stg._id}>
                                {stg.name}
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>

                      {/* Assigned To */}
                      <Col md={6}>
                        <FormGroup>
                          <Label for="page-lead-assigned" className="fw-semibold">
                            Assigned To
                          </Label>
                          <Input
                            type="select"
                            id="page-lead-assigned"
                            name="assignedTo"
                            value={formData.assignedTo}
                            onChange={handleChange}
                          >
                            {isSuperAdmin ? (
                              <option value="">-- Auto Assign / Unassigned --</option>
                            ) : (
                              <option value={authUser?._id || ""}>Self ({authUser?.firstName} {authUser?.lastName})</option>
                            )}
                            {users.map((usr) => (
                              <option key={usr._id} value={usr._id}>
                                {usr.firstName} {usr.lastName} ({usr.role || "Employee"})
                              </option>
                            ))}
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>



                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Button color="light" onClick={() => navigate(-1)} disabled={submitting}>
                        Cancel
                      </Button>
                      <Button color="primary" type="submit" className="px-4" disabled={submitting}>
                        {submitting ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Creating...
                          </>
                        ) : (
                          <>
                            <i className="mdi mdi-check me-1" /> Save Lead
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CreateLeadPage;
